/**
 * API Client for backend communication
 * Handles authentication, request/response interceptors, and API calls
 */

import { supabase } from './supabase';
import { API_CONFIG } from './apiConfig';
import { API_ENDPOINTS, buildUrlWithParams } from './apiEndpoints';
import { createApiError, NetworkError } from './errors';

/**
 * Merge one or more `include` tokens into a comma-separated include string.
 * De-dupes and preserves any caller-provided tokens. Used by aircraft /
 * quote / bid / flight fetchers so we can always request `custom_profile`
 * (and its nested paths under bid_items.bid_options.aircraft, etc.)
 * without clobbering includes the caller already asked for.
 *
 *   mergeInclude('operator,photos', 'custom_profile')
 *     → 'operator,photos,custom_profile'
 *   mergeInclude(undefined, ['aircraft.custom_profile', 'operator'])
 *     → 'aircraft.custom_profile,operator'
 */
const mergeInclude = (existing, additions) => {
  const set = new Set();
  if (typeof existing === 'string' && existing.trim()) {
    existing.split(',').map((s) => s.trim()).filter(Boolean).forEach((t) => set.add(t));
  }
  const toAdd = Array.isArray(additions) ? additions : [additions];
  toAdd.filter(Boolean).forEach((t) => set.add(t));
  return [...set].join(',');
};

/**
 * @typedef {Object} RequestOptions
 * @property {string} [method] - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @property {Object} [headers] - Additional headers
 * @property {*} [body] - Request body
 * @property {number} [timeout] - Request timeout in milliseconds
 * @property {boolean} [skipAuth] - Skip authentication header
 * @property {AbortSignal} [signal] - Abort signal for cancellation
 * @property {string} [baseUrl] - Override the default base URL for this request
 */

/**
 * API Client class
 * Provides methods for making authenticated HTTP requests to the backend
 */
class ApiClient {
  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
    this.defaultHeaders = API_CONFIG.DEFAULT_HEADERS;
    this.requestInterceptors = [];
    this.responseInterceptors = [];
  }

  /**
   * Get authentication token from Supabase
   * @returns {Promise<string|null>} Access token or null
   * @private
   */
  async getAuthToken() {
    try {
      console.log('API: Getting auth token...');

      // Fast path: read session from localStorage and use it if not expired.
      // Key format: sb-{project-ref}-auth-token
      const authTokenKey = Object.keys(localStorage).find(key => key.startsWith('sb-') && key.endsWith('-auth-token'));
      const storedSession = authTokenKey ? localStorage.getItem(authTokenKey) : null;
      let parsedSession = null;
      if (storedSession) {
        try {
          parsedSession = JSON.parse(storedSession);
          const accessToken = parsedSession?.access_token;
          const expiresAt = parsedSession?.expires_at;

          // Valid (with 60s buffer) — use as-is
          if (accessToken && expiresAt && (expiresAt * 1000) > (Date.now() + 60000)) {
            console.log('API: Using cached token from localStorage');
            return accessToken;
          }
        } catch (parseError) {
          console.warn('API: Failed to parse stored session:', parseError);
        }
      }

      // Token is expired/near-expiry. Proactively refresh rather than letting
      // an expired token hit the backend (getSession() does NOT refresh).
      if (parsedSession?.refresh_token) {
        console.log('API: Cached token expired, refreshing session...');
        const { data: { session }, error } = await this._refreshSession();
        if (error) {
          console.error('API: Proactive refresh failed:', error.message);
        }
        if (session?.access_token) {
          return session.access_token;
        }
      }

      // Last resort: ask Supabase (may return null if not signed in)
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token || null;
    } catch (error) {
      console.error('API: Error getting auth token:', error.message);
      return null;
    }
  }

  /**
   * Refresh the Supabase session, deduplicating concurrent callers so we
   * don't fire multiple refresh requests in parallel.
   * @returns {Promise<{data: {session: Object|null}, error: Error|null}>}
   * @private
   */
  _refreshSession() {
    if (!this._refreshPromise) {
      this._refreshPromise = supabase.auth.refreshSession().finally(() => {
        this._refreshPromise = null;
      });
    }
    return this._refreshPromise;
  }

  /**
   * Build complete URL with base URL and endpoint
   * @param {string} endpoint - API endpoint
   * @param {string} [baseUrl] - Optional base URL override
   * @returns {string} Complete URL
   * @private
   */
  buildUrl(endpoint, baseUrl) {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const root = baseUrl || this.baseUrl;
    return `${root}${cleanEndpoint}`;
  }

  /**
   * Build request headers
   * @param {Object} additionalHeaders - Additional headers to merge
   * @param {boolean} skipAuth - Skip adding auth token
   * @returns {Promise<Object>} Headers object
   * @private
   */
  async buildHeaders(additionalHeaders = {}, skipAuth = false) {
    const headers = {
      ...this.defaultHeaders,
      ...additionalHeaders,
    };

    if (!skipAuth) {
      const token = await this.getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Create abort controller with timeout
   * @param {number} timeout - Timeout in milliseconds
   * @returns {AbortController} Abort controller
   * @private
   */
  createAbortController(timeout) {
    const controller = new AbortController();

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    // Clear timeout when request completes
    controller.signal.addEventListener('abort', () => {
      clearTimeout(timeoutId);
    });

    return controller;
  }

  /**
   * Handle fetch errors
   * @param {Error} error - Fetch error
   * @throws {NetworkError|ApiError}
   * @private
   */
  handleFetchError(error) {
    if (error.name === 'AbortError') {
      throw new NetworkError('Request timeout', { originalError: error });
    }

    if (!navigator.onLine) {
      throw new NetworkError('No internet connection', { originalError: error });
    }

    throw new NetworkError('Network request failed', { originalError: error });
  }

  /**
   * Parse response body
   * @param {Response} response - Fetch response
   * @returns {Promise<*>} Parsed response body
   * @private
   */
  async parseResponse(response) {
    const contentType = response.headers.get('content-type');

    if (contentType?.includes('application/json')) {
      return response.json();
    }

    if (contentType?.includes('text/')) {
      return response.text();
    }

    return response.blob();
  }

  /**
   * Handle HTTP error responses
   * @param {Response} response - Fetch response
   * @throws {ApiError}
   * @private
   */
  async handleErrorResponse(response) {
    let errorData;

    try {
      errorData = await this.parseResponse(response);
    } catch {
      errorData = { detail: 'An error occurred' };
    }

    const message = errorData?.detail || errorData?.message || errorData?.error || `HTTP error! status: ${response.status}`;
    const details = errorData?.errors || errorData;

    throw createApiError(response.status, message, details);
  }

  /**
   * Core request method
   * @param {string} endpoint - API endpoint
   * @param {RequestOptions} options - Request options
   * @returns {Promise<*>} Response data
   * @throws {ApiError|NetworkError}
   */
  async request(endpoint, options = {}, isRetry = false, timeoutRetries = 0) {
    const MAX_TIMEOUT_RETRIES = 3;
    const {
      method = 'GET',
      headers: additionalHeaders = {},
      body,
      timeout = this.timeout,
      skipAuth = false,
      signal,
      baseUrl,
    } = options;

    try {
      console.log('API: Starting request to', endpoint);
      const url = this.buildUrl(endpoint, baseUrl);
      console.log('API: Built URL:', url);
      const headers = await this.buildHeaders(additionalHeaders, skipAuth);
      console.log('API: Headers built, making fetch...');

      // Create abort controller for timeout if no signal provided
      const abortController = signal ? null : this.createAbortController(timeout);
      const finalSignal = signal || abortController?.signal;

      const fetchOptions = {
        method,
        headers,
        signal: finalSignal,
      };

      // Add body for non-GET requests
      if (body && method !== 'GET' && method !== 'HEAD') {
        if (body instanceof FormData) {
          // Remove Content-Type for FormData (browser sets it with boundary)
          delete fetchOptions.headers['Content-Type'];
          fetchOptions.body = body;
        } else if (typeof body === 'object') {
          fetchOptions.body = JSON.stringify(body);
        } else {
          fetchOptions.body = body;
        }
      }

      // Execute request interceptors
      let modifiedOptions = fetchOptions;
      for (const interceptor of this.requestInterceptors) {
        modifiedOptions = await interceptor(url, modifiedOptions);
      }

      // Make the request
      const response = await fetch(url, modifiedOptions);

      // Handle 401 errors with retry after session refresh
      if (response.status === 401 && !isRetry && !skipAuth) {
        console.log('API: 401 received, refreshing session and retrying...');
        try {
          await this._refreshSession();
          return this.request(endpoint, options, true);
        } catch (refreshError) {
          console.error('API: Session refresh failed:', refreshError);
        }
      }

      // Handle error responses
      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      // Parse response
      const data = await this.parseResponse(response);

      // Execute response interceptors
      let modifiedData = data;
      for (const interceptor of this.responseInterceptors) {
        modifiedData = await interceptor(response, modifiedData);
      }

      return modifiedData;
    } catch (error) {
      // Retry on timeout up to MAX_TIMEOUT_RETRIES times with exponential backoff
      // (1s, 2s, 4s). AbortError signals either a timeout or a caller-initiated
      // cancellation — only retry the timeout case (when we own the AbortController
      // and no external signal was passed).
      if (error.name === 'AbortError' && !signal && timeoutRetries < MAX_TIMEOUT_RETRIES) {
        const backoffMs = 2 ** timeoutRetries * 1000;
        console.warn(`API: Request timed out, retrying in ${backoffMs}ms (${timeoutRetries + 1}/${MAX_TIMEOUT_RETRIES})...`);
        await new Promise(resolve => setTimeout(resolve, backoffMs));
        return this.request(endpoint, options, isRetry, timeoutRetries + 1);
      }

      // Handle network errors
      if (error.name === 'TypeError' || error.name === 'AbortError') {
        this.handleFetchError(error);
      }

      // Re-throw API errors
      throw error;
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async get(endpoint, params = {}, options = {}) {
    console.log('GET params:', params, 'keys:', Object.keys(params));
    const url = Object.keys(params).length > 0 ? buildUrlWithParams(endpoint, params) : endpoint;
    console.log('GET url:', url);
    return this.request(url, { ...options, method: 'GET' });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {*} body - Request body
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async post(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'POST', body });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {*} body - Request body
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async put(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PUT', body });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {*} body - Request body
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async patch(endpoint, body, options = {}) {
    return this.request(endpoint, { ...options, method: 'PATCH', body });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {RequestOptions} options - Additional options
   * @returns {Promise<*>} Response data
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Add request interceptor
   * @param {Function} interceptor - Interceptor function
   */
  addRequestInterceptor(interceptor) {
    this.requestInterceptors.push(interceptor);
  }

  /**
   * Add response interceptor
   * @param {Function} interceptor - Interceptor function
   */
  addResponseInterceptor(interceptor) {
    this.responseInterceptors.push(interceptor);
  }

  // ========================================
  // User API Methods
  // ========================================

  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Created user
   */
  async registerUser(userData) {
    return this.post(API_ENDPOINTS.USERS.REGISTER, userData);
  }

  /**
   * Get user by ID
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User data
   */
  async getUser(userId) {
    return await this.get(API_ENDPOINTS.USERS.BY_ID(userId));
  }

  /**
   * List users (team members), optionally scoped by org_id and paginated.
   * Mirrors the mobile InviteTeamScreen call: GET /users?org_id=...&skip=...&limit=...
   * @param {Object} params
   * @param {string} [params.org_id] - Organization ID to scope the list
   * @param {number} [params.skip=0]
   * @param {number} [params.limit=50]
   * @returns {Promise<Array>}
   */
  async getUsers({ org_id, email, role, status, skip = 0, limit = 50 } = {}) {
    // email is a LIKE search on the BE — consumers that need exact
    // match should filter the response client-side (see findUserByEmail).
    const filters = {
      skip,
      limit,
      ...(org_id ? { org_id } : {}),
      ...(email ? { email } : {}),
      ...(role ? { role } : {}),
      ...(status ? { status } : {}),
    };
    const data = await this.get(API_ENDPOINTS.USERS.BASE, filters);
    return Array.isArray(data) ? data : (data?.data || []);
  }

  /**
   * Exact-match user lookup by email. Wraps `getUsers({ email })` — which
   * is a LIKE search on the BE — and filters the response client-side
   * for a case-insensitive exact hit. Used by the Add Member pre-flight
   * check that intercepts archived-then-reinvite collisions before an
   * invite email is fired.
   *
   * Guards against pathologically short strings that would return an
   * expensive substring result set — a real email is at least 5 chars
   * and contains '@'.
   *
   * @param {string} email
   * @returns {Promise<Array>} matches (0 or 1 in practice under UNIQUE(email))
   */
  async findUserByEmail(email) {
    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized || normalized.length < 5 || normalized.length > 254 || !normalized.includes('@')) {
      return [];
    }
    const rows = await this.getUsers({ email: normalized, limit: 100 });
    return rows.filter((r) => String(r?.email || '').toLowerCase() === normalized);
  }

  /**
   * Get user by user ID (new endpoint)
   * @param {string} userId - User ID
   * @returns {Promise<Object>} User data
   */
  async getUserByUserId(userId) {
    return await this.get(API_ENDPOINTS.USERS.BY_USER_ID(userId));
  }

  /**
   * Update user
   * @param {string} userId - User ID
   * @param {Object} userData - Updated user data
   * @returns {Promise<Object>} Updated user
   */
  async updateUser(userId, userData) {
    return this.patch(API_ENDPOINTS.USERS.BY_ID(userId), userData);
  }

  /**
   * Reset a user's MFA / TOTP factors. Admin-only recovery for locked-out
   * users. The user is prompted to re-enroll on their next sign-in.
   * @param {string} userId - Supabase auth.users.id of the target user
   *   (NOT userprofile_id — Supabase's MFA API keys off the auth-user id).
   * @returns {Promise<Object>} BE ack
   */
  async resetUserMfa(userId) {
    return this.patch(API_ENDPOINTS.USERS.SECURITY_MFA(userId), { reset: true });
  }

  /**
   * Get current user profile
   * @returns {Promise<Object>} User profile
   */
  async getUserProfile() {
    return this.get(API_ENDPOINTS.USERS.PROFILE);
  }

  /**
   * Logout the current user (server-side session cleanup).
   * @returns {Promise<Object>} Logout response
   */
  async logoutUser() {
    return this.post(API_ENDPOINTS.USERS.LOGOUT, {});
  }

  // ========================================
  // Operator API Methods
  // ========================================

  /**
   * Get list of operators
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (name, dba_name, certificate_number, etc.)
   * @returns {Promise<Array>} List of operators
   */
  async getOperators(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.OPERATORS.BASE, { skip, limit, ...filters });
  }

  /**
   * Get operator by ID
   * @param {string} operatorId - Operator ID
   * @param {Object} params - Optional query parameters (e.g., { include: 'ntsb_incidents,ucc_verifications' })
   * @returns {Promise<Object>} Operator data
   */
  async getOperatorById(operatorId, params = {}) {
    console.log('getOperatorById called with:', operatorId, params);
    return this.get(API_ENDPOINTS.OPERATORS.BY_ID(operatorId), params);
  }

  /**
   * Fetch the operator's TrustScore breakdown.
   * Response includes overall + per-facet scores AND the factors object
   * with tail_breakdown + fleet_breakdown component maps.
   * @param {string} operatorId - Operator ID
   * @returns {Promise<Object>} TrustScore row
   */
  async getOperatorTrustScores(operatorId) {
    return this.get(API_ENDPOINTS.OPERATORS.TRUST_SCORES(operatorId));
  }

  /**
   * Create or replace a per-org custom profile (branding override) for a
   * platform-catalog aircraft. Aircraft must have `organization_id: null`
   * — profiles only apply to shared catalog aircraft, not org-owned ones.
   * Returns 201 on create, 200 on update.
   *
   * @param {string} orgId
   * @param {string} aircraftId
   * @param {Object} body - { name, description, image_urls, banner_image_url, extra_attributes }
   * @returns {Promise<Object>} The stored custom_profile row
   */
  async upsertCustomAircraftProfile(orgId, aircraftId, body) {
    return this.post(API_ENDPOINTS.ORGANIZATIONS.AIRCRAFT_CUSTOM_PROFILE(orgId, aircraftId), body);
  }

  /**
   * Delete the per-org custom profile for a platform-catalog aircraft.
   * After deletion, reads of this aircraft return `custom_profile: null`
   * and every consumer falls back to the base aircraft record.
   *
   * @param {string} orgId
   * @param {string} aircraftId
   */
  async deleteCustomAircraftProfile(orgId, aircraftId) {
    return this.delete(API_ENDPOINTS.ORGANIZATIONS.AIRCRAFT_CUSTOM_PROFILE(orgId, aircraftId));
  }

  /**
   * Create new operator
   * @param {Object} operatorData - Operator data
   * @returns {Promise<Object>} Created operator
   */
  async createOperator(operatorData) {
    return this.post(API_ENDPOINTS.OPERATORS.BASE, operatorData);
  }

  /**
   * Update operator
   * @param {string} operatorId - Operator ID
   * @param {Object} operatorData - Updated operator data
   * @returns {Promise<Object>} Updated operator
   */
  async updateOperator(operatorId, operatorData) {
    return this.patch(API_ENDPOINTS.OPERATORS.BY_ID(operatorId), operatorData);
  }

  /**
   * Delete operator
   * @param {string} operatorId - Operator ID
   * @returns {Promise<void>}
   */
  async deleteOperator(operatorId) {
    return this.delete(API_ENDPOINTS.OPERATORS.BY_ID(operatorId));
  }

  /**
   * Search operators
   * @param {string} query - Search query
   * @returns {Promise<Array>} Search results
   */
  async searchOperators(query) {
    return this.get(API_ENDPOINTS.OPERATORS.SEARCH, { q: query });
  }

  // ========================================
  // Aircraft API Methods
  // ========================================

  /**
   * Get list of aircraft
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (tail_number, operator_id, make_model, etc.)
   * @returns {Promise<Array>} List of aircraft
   */
  async getAircrafts(skip = 0, limit = 100, filters = {}) {
    // custom_profile carries the caller-org's branding override for
    // platform-catalog aircraft. Always requested so downstream readers
    // (aircraft cards, drawers, emails, PDFs) surface it uniformly via
    // getEffectiveAircraft. amenities/features are joined so /tails list
    // rows + detail drawer render structured amenity toggles and
    // features (tier / custom_features / no_aog). make_model + operator
    // are joined so the manual-add wizard's Edit-mode hydration and the
    // /tails header (operator name) work without a second fetch.
    return this.get(API_ENDPOINTS.AIRCRAFT.BASE, {
      skip,
      limit,
      ...filters,
      include: mergeInclude(
        filters.include,
        ['custom_profile', 'amenities', 'features', 'make_model', 'operator'],
      ),
    });
  }

  /**
   * Get aircraft by ID
   * @param {string} aircraftId - Aircraft ID or make_model
   * @param {Object} params - Optional parameters
   * @param {string} params.filter - Filter type (e.g., 'make_model')
   * @returns {Promise<Object>} Aircraft data
   */
  async getAircraftById(aircraftId, params = {}) {
    return this.get(API_ENDPOINTS.AIRCRAFT.BY_ID(aircraftId), {
      ...params,
      include: mergeInclude(
        params.include,
        ['custom_profile', 'amenities', 'features', 'make_model', 'operator'],
      ),
    });
  }

  /**
   * Get aircraft amenities
   * @param {string} aircraftId - Aircraft ID
   * @returns {Promise<Object>} Aircraft amenities data
   */
  async getAircraftAmenities(aircraftId) {
    return this.get(API_ENDPOINTS.AIRCRAFT.AMENITIES(aircraftId));
  }

  /**
   * Get aircraft photos
   * @param {string} aircraftId - Aircraft ID
   * @returns {Promise<Array>} Aircraft photos data
   */
  async getAircraftPhotos(aircraftId) {
    return this.get(API_ENDPOINTS.AIRCRAFT.PHOTOS(aircraftId));
  }

  /**
   * Create new aircraft
   * @param {Object} aircraftData - Aircraft data
   * @returns {Promise<Object>} Created aircraft
   */
  async createAircraft(aircraftData) {
    return this.post(API_ENDPOINTS.AIRCRAFT.BASE, aircraftData);
  }

  /**
   * Update aircraft
   * @param {string} aircraftId - Aircraft ID
   * @param {Object} aircraftData - Updated aircraft data
   * @returns {Promise<Object>} Updated aircraft
   */
  async updateAircraft(aircraftId, aircraftData) {
    return this.patch(API_ENDPOINTS.AIRCRAFT.BY_ID(aircraftId), aircraftData);
  }

  /**
   * Delete aircraft
   * @param {string} aircraftId - Aircraft ID
   * @returns {Promise<void>}
   */
  async deleteAircraft(aircraftId) {
    return this.delete(API_ENDPOINTS.AIRCRAFT.BY_ID(aircraftId));
  }

  /**
   * Search aircraft by tail number
   * @param {string} tailNumber - Aircraft tail number (partial match, case-insensitive)
   * @returns {Promise<Array>} Aircraft data
   */
  async searchAircraftByTail(tailNumber) {
    return this.get(API_ENDPOINTS.AIRCRAFT.BASE, {
      tail_number: tailNumber,
      include: mergeInclude('operator,features,photos,make_model', ['custom_profile', 'amenities']),
    });
  }

  // ==================== Aircraft Sub-Resources (features, amenities) ====================
  // Used by the Add-Aircraft-Manually wizard's Save flow. Each sub-resource
  // is a separate row keyed by aircraft_id (POST once per created aircraft).

  async createAircraftFeatures(aircraftId, body) {
    return this.post(API_ENDPOINTS.AIRCRAFT.FEATURES(aircraftId), body);
  }
  async updateAircraftFeatures(aircraftId, body) {
    return this.patch(API_ENDPOINTS.AIRCRAFT.FEATURES(aircraftId), body);
  }
  async deleteAircraftFeatures(aircraftId) {
    return this.delete(API_ENDPOINTS.AIRCRAFT.FEATURES(aircraftId));
  }

  async createAircraftAmenities(aircraftId, body) {
    return this.post(API_ENDPOINTS.AIRCRAFT.AMENITIES(aircraftId), body);
  }
  async updateAircraftAmenities(aircraftId, body) {
    return this.patch(API_ENDPOINTS.AIRCRAFT.AMENITIES(aircraftId), body);
  }
  async deleteAircraftAmenities(aircraftId) {
    return this.delete(API_ENDPOINTS.AIRCRAFT.AMENITIES(aircraftId));
  }

  // ==================== Aircraft Make/Models (reference catalog) ====================

  /**
   * Search the make/model catalog. All filters are partial-match,
   * case-insensitive, and combined with AND on the BE — the wizard's
   * two-field search compensates by firing two calls and unioning.
   */
  async getAircraftMakeModels(filters = {}) {
    return this.get(API_ENDPOINTS.AIRCRAFT_MAKE_MODELS.BASE, filters);
  }
  async createAircraftMakeModel(body) {
    return this.post(API_ENDPOINTS.AIRCRAFT_MAKE_MODELS.BASE, body);
  }
  async updateAircraftMakeModel(makeModelId, body) {
    return this.patch(API_ENDPOINTS.AIRCRAFT_MAKE_MODELS.BY_ID(makeModelId), body);
  }
  async deleteAircraftMakeModel(makeModelId) {
    return this.delete(API_ENDPOINTS.AIRCRAFT_MAKE_MODELS.BY_ID(makeModelId));
  }

  // ==================== Org-Scoped Aircraft ====================
  // Manually-created aircraft owned by a specific organization (as
  // opposed to the shared platform catalog under /aircrafts). The wizard
  // creates one here as step 2 of its save chain.

  async createAircraftForOrganization(orgId, body) {
    return this.post(API_ENDPOINTS.ORGANIZATIONS.AIRCRAFTS(orgId), body);
  }
  async updateAircraftForOrganization(orgId, aircraftId, body) {
    return this.patch(API_ENDPOINTS.ORGANIZATIONS.AIRCRAFT_BY_ID(orgId, aircraftId), body);
  }
  async deleteAircraftForOrganization(orgId, aircraftId) {
    return this.delete(API_ENDPOINTS.ORGANIZATIONS.AIRCRAFT_BY_ID(orgId, aircraftId));
  }

  // ========================================
  // JetNet API Methods
  // ========================================

  /**
   * Search JetNet for aircraft by tail number
   * @param {string} tailNumber - Aircraft tail number
   * @returns {Promise<Array>} Aircraft data from JetNet
   */
  async searchJetNetAircraft(tailNumber) {
    return this.get(API_ENDPOINTS.JETNET.AIRCRAFTS, { tail_number: tailNumber });
  }

  /**
   * Create/import a JetNet aircraft record
   * @param {Object} aircraftData - Aircraft object from JetNet search
   * @returns {Promise<Object>} Created aircraft data
   */
  async createJetNetAircraft(aircraftData) {
    return this.post(API_ENDPOINTS.JETNET.AIRCRAFTS, aircraftData);
  }

  // ========================================
  // Organization API Methods
  // ========================================

  /**
   * Get list of organizations
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {string[]} [includes] - Optional server-side relation loaders
   *   (e.g. ['platform_fee_brackets', 'default_platform_fee_brackets']).
   *   Joined into a comma-separated `include=` query param. Used by the
   *   super-admin Organizations page to avoid N+1 fee-bracket lookups.
   * @returns {Promise<Array>} List of organizations
   */
  /**
   * List organizations. Two call shapes, both supported:
   *   1. Legacy positional — getOrganizations(skip, limit, includes[])
   *   2. Options bag — getOrganizations({ skip, limit, search, type,
   *      subscription_tier, sort_by, sort_dir, include })
   *
   * The options bag omits empty-string values so the URL stays clean.
   */
  async getOrganizations(...args) {
    const opts = (args.length === 1 && args[0] && typeof args[0] === 'object' && !Array.isArray(args[0]))
      ? args[0]
      : {
          skip: args[0] || 0,
          limit: args[1] || 100,
          include: Array.isArray(args[2]) && args[2].length > 0 ? args[2].join(',') : undefined,
        };

    const params = {
      skip: opts.skip ?? 0,
      limit: opts.limit ?? 100,
    };
    // Only send optional params when the caller actually set them —
    // avoids `?search=&type=&subscription_tier=` noise on the wire.
    if (opts.search) params.search = opts.search;
    if (opts.type) params.type = opts.type;
    if (opts.subscription_tier) params.subscription_tier = opts.subscription_tier;
    if (opts.sort_by) params.sort_by = opts.sort_by;
    if (opts.sort_dir) params.sort_dir = opts.sort_dir;
    if (opts.include) {
      params.include = Array.isArray(opts.include) ? opts.include.join(',') : opts.include;
    }
    return this.get(API_ENDPOINTS.ORGANIZATIONS.BASE, params);
  }

  /**
   * Get organization by ID
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object>} Organization data
   */
  async getOrganizationById(organizationId, params = {}) {
    // params may carry an `include` string (e.g. 'platform_fee_brackets')
    // — passed through as-is so callers can opt into extra joins.
    return this.get(API_ENDPOINTS.ORGANIZATIONS.BY_ID(organizationId), params);
  }

  /**
   * Get organization by ID using checkout token (for public access)
   * @param {string} organizationId - Organization ID
   * @param {string} token - Checkout token for authentication
   * @returns {Promise<Object>} Organization data
   */
  async getOrganizationByIdWithToken(organizationId, token) {
    return this.get(
      API_ENDPOINTS.ORGANIZATIONS.BY_ID(organizationId),
      token ? { token } : {},
      token ? { skipAuth: true } : {}
    );
  }

  async getUserByIdWithToken(userId, token) {
    return this.get(
      API_ENDPOINTS.USERS.BY_ID(userId),
      token ? { token } : {},
      token ? { skipAuth: true } : {}
    );
  }

  /**
   * Create new organization
   * @param {Object} organizationData - Organization data
   * @returns {Promise<Object>} Created organization
   */
  async createOrganization(organizationData) {
    return this.post(API_ENDPOINTS.ORGANIZATIONS.BASE, organizationData);
  }

  /**
   * Update organization
   * @param {string} organizationId - Organization ID
   * @param {Object} organizationData - Updated organization data
   * @returns {Promise<Object>} Updated organization
   */
  async updateOrganization(organizationId, organizationData) {
    return this.patch(API_ENDPOINTS.ORGANIZATIONS.BY_ID(organizationId), organizationData);
  }

  /**
   * Delete organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<void>}
   */
  async deleteOrganization(organizationId) {
    return this.delete(API_ENDPOINTS.ORGANIZATIONS.BY_ID(organizationId));
  }

  // ========================================
  // Platform-fee brackets (per-org dollar-tier overrides). Each row
  // is {min_amount, max_amount, fee_percent}; max_amount = 0 = unbounded.
  // BE returns amounts as strings — normalise with Number() at read.
  // ========================================

  /**
   * List all platform-fee brackets for an organization.
   * @param {string} organizationId
   * @returns {Promise<Array>}
   */
  async listPlatformFeeBrackets(organizationId) {
    return this.get(API_ENDPOINTS.ORGANIZATIONS.PLATFORM_FEE_BRACKETS(organizationId));
  }

  /**
   * Get a single platform-fee bracket by id.
   * @param {string} organizationId
   * @param {string} bracketId
   * @returns {Promise<Object>}
   */
  async getPlatformFeeBracket(organizationId, bracketId) {
    return this.get(
      API_ENDPOINTS.ORGANIZATIONS.PLATFORM_FEE_BRACKET_BY_ID(organizationId, bracketId),
    );
  }

  /**
   * Append one platform-fee bracket to an organization.
   * @param {string} organizationId
   * @param {{min_amount:number, max_amount:number, fee_percent:number}} payload
   * @returns {Promise<Object>} created bracket (with server-generated id)
   */
  async createPlatformFeeBracket(organizationId, payload) {
    return this.post(
      API_ENDPOINTS.ORGANIZATIONS.PLATFORM_FEE_BRACKETS(organizationId),
      payload,
    );
  }

  /**
   * Update one platform-fee bracket. Send only the fields you want to
   * change — BE PATCH merges into the existing row.
   * @param {string} organizationId
   * @param {string} bracketId
   * @param {Partial<{min_amount:number, max_amount:number, fee_percent:number}>} patch
   * @returns {Promise<Object>} updated bracket
   */
  async updatePlatformFeeBracket(organizationId, bracketId, patch) {
    return this.patch(
      API_ENDPOINTS.ORGANIZATIONS.PLATFORM_FEE_BRACKET_BY_ID(organizationId, bracketId),
      patch,
    );
  }

  /**
   * Delete one platform-fee bracket.
   * @param {string} organizationId
   * @param {string} bracketId
   * @returns {Promise<void>}
   */
  async deletePlatformFeeBracket(organizationId, bracketId) {
    return this.delete(
      API_ENDPOINTS.ORGANIZATIONS.PLATFORM_FEE_BRACKET_BY_ID(organizationId, bracketId),
    );
  }

  /**
   * Delete ALL platform-fee brackets for an organization. Destructive —
   * the UI must confirm before calling this.
   * @param {string} organizationId
   * @returns {Promise<void>}
   */
  async deleteAllPlatformFeeBrackets(organizationId) {
    return this.delete(
      API_ENDPOINTS.ORGANIZATIONS.PLATFORM_FEE_BRACKETS(organizationId),
    );
  }

  // ========================================
  // Upload Methods
  // ========================================

  /**
   * Upload a file
   * @param {File} file - File object to upload
   * @param {string} path - Upload destination path
   * @param {string} [checkoutToken] - Optional checkout token for public access (sent as X-Checkout-Token header — this endpoint does NOT accept ?token= in the URL)
   * @returns {Promise<Object>} Upload result
   */
  async uploadFile(file, path, checkoutToken = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('path', path);
    if (checkoutToken) {
      return this.post(API_ENDPOINTS.UPLOADS.BASE, formData, {
        baseUrl: process.env.REACT_APP_IMAGE_BASE_URL,
        headers: { 'X-Checkout-Token': checkoutToken },
        skipAuth: true,
      });
    }
    return this.post(API_ENDPOINTS.UPLOADS.BASE, formData, {
      baseUrl: process.env.REACT_APP_IMAGE_BASE_URL,
    });
  }

  // ========================================
  // LLM API Methods
  // ========================================

  /**
   * Get estimated arrival time for a flight
   * @param {Object} params - { from_location, to_location, departure_time }
   * @returns {Promise<Object>} { from_location, to_location, departure_time, arrival_time }
   */
  async getArrivalTime(params) {
    return this.post(API_ENDPOINTS.LLM.ARRIVAL_TIME, params);
  }

  // ========================================
  // Stripe Connect API Methods
  // ========================================

  /**
   * Get Stripe Connected Account status for an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Stripe account status
   */
  async getStripeAccountStatus(orgId) {
    return this.get(API_ENDPOINTS.STRIPE.CONNECTED_ACCOUNT(orgId));
  }

  /**
   * Start Stripe onboarding for an organization
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Onboarding details with URL
   */
  async startStripeOnboarding(orgId) {
    return this.post(API_ENDPOINTS.STRIPE.ONBOARDING_START(orgId));
  }

  /**
   * Create a Stripe payment link
   * @param {Object} payload - Payment link details
   * @returns {Promise<Object>} Payment link response with URL
   */
  async createPaymentLink(payload) {
    return this.post(API_ENDPOINTS.STRIPE.PAYMENT_LINKS, payload);
  }

  /**
   * Create an MX Merchant payment link
   * @param {Object} payload - Payment link details
   * @returns {Promise<Object>} Payment link response with URL
   */
  async createMxMerchantPaymentLink(payload, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(buildUrlWithParams(API_ENDPOINTS.MXMERCHANT.PAYMENT_LINKS, { token: checkoutToken }), payload, {
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.MXMERCHANT.PAYMENT_LINKS, payload);
  }

  /**
   * Capture an MX Merchant payment transaction
   * @param {Object} payload - Capture details (payment_id, transId, refNum)
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Capture response
   */
  async captureMxMerchantPayment(payload, checkoutToken = null) {
    if (checkoutToken) {
      return this.patch(buildUrlWithParams(API_ENDPOINTS.MXMERCHANT.CAPTURE_PAYMENT, { token: checkoutToken }), payload, {
        skipAuth: true
      });
    }
    return this.patch(API_ENDPOINTS.MXMERCHANT.CAPTURE_PAYMENT, payload);
  }

  /**
   * Complete an authorized MX Merchant sale (capture the pre-auth hold).
   * @param {{quote_id?: string, invoice_id?: string}} payload
   * @returns {Promise<Object>}
   */
  async saleCompletionMxMerchantPayment(payload) {
    return this.patch(API_ENDPOINTS.MXMERCHANT.SALE_COMPLETION, payload);
  }

  /**
   * Capture a Priority Commerce (Passport) checkout payment.
   * @param {Object} payload - Capture details
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Capture response
   */
  async capturePassportPayment(payload, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(buildUrlWithParams(API_ENDPOINTS.PASSPORT.CAPTURE_PAYMENT, { token: checkoutToken }), payload, {
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.PASSPORT.CAPTURE_PAYMENT, payload);
  }

  /**
   * Fetch Priority Commerce client secrets for the wire/ACH payment widget.
   * @param {Object} payload
   * @param {string|null} checkoutToken - public checkout token (token-based flow)
   * @returns {Promise<Object>} { client_secret, business_id, ... }
   */
  async getClientSecrets(payload = {}, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(buildUrlWithParams(API_ENDPOINTS.CLIENTS.CLIENT_SECRETS, { token: checkoutToken }), payload, {
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.CLIENTS.CLIENT_SECRETS, payload);
  }

  // ========================================
  // Bids API Methods
  // ========================================

  /**
   * Get list of bids
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (status, client_id, etc.)
   * @returns {Promise<Array>} List of bids
   */
  async getBids(skip = 0, limit = 100, filters = {}, options = {}) {
    return this.get(
      API_ENDPOINTS.BIDS.BASE,
      { ...filters, include: mergeInclude(filters.include, 'bid_options.aircraft.custom_profile') },
      options,
    );
  }

  /**
   * Get bid by ID
   * @param {string} bidId - Bid ID
   * @returns {Promise<Object>} Bid data
   */
  async getBidById(bidId) {
    return this.get(API_ENDPOINTS.BIDS.BY_ID(bidId), {
      include: 'bid_options.aircraft.custom_profile',
    });
  }

  /**
   * Create new bid
   * @param {Object} bidData - Bid data
   * @returns {Promise<Object>} Created bid
   */
  async createBid(bidData) {
    return this.post(API_ENDPOINTS.BIDS.BASE, bidData);
  }

  /**
   * Update bid
   * @param {string} bidId - Bid ID
   * @param {Object} bidData - Updated bid data
   * @returns {Promise<Object>} Updated bid
   */
  async updateBid(bidId, bidData) {
    return this.patch(API_ENDPOINTS.BIDS.BY_ID(bidId), bidData);
  }

  /**
   * Delete bid
   * @param {string} bidId - Bid ID
   * @returns {Promise<void>}
   */
  async deleteBid(bidId) {
    return this.delete(API_ENDPOINTS.BIDS.BY_ID(bidId));
  }

  /**
   * Get shortlisted bids
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @returns {Promise<Array>} Shortlisted bids
   */
  async getShortlistedBids(skip = 0, limit = 100) {
    return this.get(API_ENDPOINTS.BIDS.SHORTLIST, {
      skip,
      limit,
      include: 'bid_options.aircraft.custom_profile',
    });
  }

  /**
   * Get list of bids options
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (status, client_id, etc.)
   * @returns {Promise<Array>} List of bids options
   */
  async getBidsOptions(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.BIDS.OPTIONS, {
      skip,
      limit,
      ...filters,
      include: mergeInclude(filters.include, 'aircraft.custom_profile'),
    });
  }

  /**
   * Create bid option
   * @param {Object} bidOptionData - Bid option data
   * @returns {Promise<Object>} Created bid option
   */
  async createBidOption(bidOptionData) {
    return this.post(API_ENDPOINTS.BIDS.OPTIONS, bidOptionData);
  }

  /**
   * Update bid option
   * @param {string} bidOptionId - Bid option ID
   * @param {Object} data - Bid option data
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Updated bid option
   */
  async updateBidOption(bidOptionId, data, checkoutToken = null) {
    if (checkoutToken) {
      return this.patch(buildUrlWithParams(API_ENDPOINTS.BIDS.OPTIONS_BY_ID(bidOptionId), { token: checkoutToken }), data, {
        skipAuth: true
      });
    }
    return this.patch(API_ENDPOINTS.BIDS.OPTIONS_BY_ID(bidOptionId), data);
  }

  /**
   * Get bid option by ID
   * @param {string} bidOptionId - Bid option ID
   * @returns {Promise<Object>} Bid option data
   */
  async getBidOptionById(bidOptionId) {
    return this.get(API_ENDPOINTS.BIDS.OPTIONS_BY_ID(bidOptionId), {
      include: 'aircraft.custom_profile',
    });
  }

  async getBidOptionByIdWithToken(bidOptionId, token) {
    return this.get(
      API_ENDPOINTS.BIDS.OPTIONS_BY_ID(bidOptionId),
      {
        include: 'aircraft.custom_profile',
        ...(token && { token }),
      },
      token ? { skipAuth: true } : {}
    );
  }

  /**
   * Get bid by bid number
   * @param {string} bidNumber - Bid number
   * @returns {Promise<Object>} Bid data
   */
  async getBidByNumber(bidNumber) {
    return this.get(API_ENDPOINTS.BIDS.BY_NUMBER(bidNumber), {
      include: 'bid_options.aircraft.custom_profile',
    });
  }

  /**
   * Get bids by broker organization
   * @param {string} brokerOrgId - Broker organization ID
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @returns {Promise<Array>} List of bids
   */
  async getBidsByBroker(brokerOrgId, skip = 0, limit = 100) {
    return this.get(API_ENDPOINTS.BIDS.BY_BROKER(brokerOrgId), {
      skip,
      limit,
      include: 'bid_options.aircraft.custom_profile',
    });
  }

  /**
   * Get bids by status
   * @param {string} status - Bid status (DRAFT, SENT, ACCEPTED, REJECTED)
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @returns {Promise<Array>} List of bids
   */
  async getBidsByStatus(status, skip = 0, limit = 100) {
    return this.get(API_ENDPOINTS.BIDS.BY_STATUS(status), {
      skip,
      limit,
      include: 'bid_options.aircraft.custom_profile',
    });
  }

  /**
   * Toggle bid shortlist status
   * @param {string} bidId - Bid ID
   * @param {boolean} shortlisted - Whether to shortlist or un-shortlist
   * @returns {Promise<Object>} Updated bid
   */
  async toggleBidShortlist(bidId, shortlisted) {
    return this.patch(API_ENDPOINTS.BIDS.BY_ID(bidId), { shortlisted });
  }

  // ==========================================================================
  // Message API Methods
  // ==========================================================================

  /**
   * Get all messages with optional filters
   * @param {number} skip - Number of messages to skip (pagination)
   * @param {number} limit - Maximum number of messages to return
   * @param {Object} filters - Optional filters (message_type, from_email, to_email, subject, etc.)
   * @returns {Promise<Array>} List of messages
   */
  async getMessages(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.MESSAGES.BASE, { skip, limit, ...filters });
  }

  /**
   * Get message by ID
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} Message object
   */
  async getMessageById(messageId) {
    return this.get(API_ENDPOINTS.MESSAGES.BY_ID(messageId));
  }

  /**
   * Get unprocessed messages
   * @param {number} limit - Maximum number of messages to return
   * @returns {Promise<Array>} List of unprocessed messages
   */
  async getUnprocessedMessages(limit = 100) {
    return this.get(API_ENDPOINTS.MESSAGES.UNPROCESSED, { limit });
  }

  /**
   * Update a message
   * @param {string} messageId - Message ID
   * @param {Object} updateData - Update data (subject, from_name, processed, etc.)
   * @returns {Promise<Object>} Updated message object
   */
  async updateMessage(messageId, updateData) {
    return this.patch(API_ENDPOINTS.MESSAGES.BY_ID(messageId), updateData);
  }

  /**
   * Mark message as processed
   * @param {string} messageId - Message ID
   * @returns {Promise<Object>} Updated message object
   */
  async markMessageProcessed(messageId) {
    return this.post(API_ENDPOINTS.MESSAGES.MARK_PROCESSED(messageId), {});
  }

  // ==================== Status Events (Notifications) ====================

  /**
   * Fetch the paginated status-event feed for the current user.
   * @param {Object} [params]
   * @param {number} [params.skip=0]
   * @param {number} [params.limit=50] Max 100 per API contract.
   * @param {string} [params.category] Comma-separated: flights,quotes,payments,invoices. Omit for all.
   * @returns {Promise<Array>} Array of status-event objects (see BE OpenAPI).
   */
  async getStatusEvents({ skip = 0, limit = 50, category } = {}) {
    const params = { skip, limit };
    if (category) params.category = category;
    return this.get(API_ENDPOINTS.STATUS_EVENTS.BASE, params);
  }

  /**
   * Unread-count for the notification bell. Scope by category filter to
   * match whatever the drawer's active filter is.
   * @param {string} [category] Comma-separated: flights,quotes,payments,invoices.
   * @returns {Promise<{unread_count: number}>}
   */
  async getStatusEventsUnreadCount(category) {
    const params = {};
    if (category) params.category = category;
    return this.get(API_ENDPOINTS.STATUS_EVENTS.UNREAD_COUNT, params);
  }

  /**
   * Mark every unread event as read. Optionally scope by category so
   * the drawer's "Read All" only clears what the current filter shows.
   * @param {string} [category]
   * @returns {Promise<{marked_count: number}>}
   */
  async markAllStatusEventsRead(category) {
    const params = {};
    if (category) params.category = category;
    return this.post(buildUrlWithParams(API_ENDPOINTS.STATUS_EVENTS.READ_ALL, params), {});
  }

  /**
   * Mark a single event as read.
   * @param {string} statusEventId
   * @returns {Promise<{status_event_id, user_id, read_at, is_read}>}
   */
  async markStatusEventRead(statusEventId) {
    return this.post(API_ENDPOINTS.STATUS_EVENTS.MARK_READ(statusEventId), {});
  }

  // ==================== Quotes ====================

  /**
   * Create a new quote
   * @param {Object} quoteData - Quote data
   * @returns {Promise<Object>} Created quote object
   */
  async createQuote(quoteData) {
    return this.post(API_ENDPOINTS.QUOTES.BASE, quoteData);
  }

  /**
   * Create a quote + its bids + bid_options in one transaction.
   *
   * Payload shape (see BE spec):
   *   { quote: {...}, bid: {...} }              — single-bid create
   *   { quote: {...}, bids: [{...}, {...}] }    — multi-bid (duplicate)
   *
   * Semantics:
   *   - Always creates a new quote (quote_id in body is ignored).
   *   - For each bid with `bid_id`: UPDATE partial trip fields, LINK to the
   *     new quote via quote_bid_items. Identity fields (created_by,
   *     broker_org_id, trip_number) are not overwritten. Unknown bid_id
   *     returns 404.
   *   - For each bid without `bid_id`: INSERT the bid + its options.
   *   - For each bid_option: with id → PATCH by id; without id → INSERT.
   *     Options omitted from the payload are NOT unshortlisted (matches
   *     trip-group generate semantics — soft-delete is PATCH-only).
   *   - Does NOT mint a checkout token or send email — those stay as
   *     separate calls in the send flow.
   *
   * Response carries the full include tree (bid_items → bid → bid_options →
   * aircraft(+custom_profile) → operator, plus passengers, client, creator,
   * sender), same shape as `getQuoteById` — safe to seed the drawer cache
   * with it directly.
   *
   * @param {Object} payload — { quote, bid | bids }
   * @returns {Promise<Object>} Created quote with nested tree
   */
  async createQuoteWithBids(payload) {
    return this.post(API_ENDPOINTS.QUOTES.WITH_BIDS, payload);
  }

  /**
   * Update a quote + its bids + bid_options in one transaction.
   *
   * Payload shape (see BE spec):
   *   { quote: {...}, bid: {...} }              — single-bid update (edit)
   *   { quote: {...}, bids: [{...}, {...}] }    — multi-bid update
   *
   * Semantics:
   *   - Updates the quote at :quote_id in place (quote.files merges).
   *   - For each bid with `bid_id`: UPDATE partial trip fields where the bid
   *     is linked to this quote. 0 rows → 400 (bid not linked).
   *   - For each bid without `bid_id`: INSERT a new bid + options and ADD a
   *     quote_bid_item (existing links preserved).
   *   - For each bid.bid_options when the key is PRESENT:
   *       * options with `bid_option_id` on this bid → PATCH
   *       * options without id → INSERT
   *       * **existing options omitted from the array → soft-unshortlisted
   *         (`shortlisted: false`)**. `bid_options: []` unshortlists ALL
   *         tails on that bid.
   *   - When `bid_options` is OMITTED from the bid payload → trip fields
   *     only; stored tails unchanged. Never send a sparse list — either the
   *     full desired-set or omit the key entirely.
   *   - Does NOT mint a checkout token or send email.
   *
   * Endpoint diffs against every stored option on the bid (not against a
   * hydrate-time snapshot), so a concurrent editor's addition would be
   * soft-deleted by our save. Rare; documented tradeoff.
   *
   * Response mirrors getQuoteById's include tree.
   *
   * @param {string} quoteId
   * @param {Object} payload — { quote, bid | bids }
   * @returns {Promise<Object>} Updated quote with nested tree
   */
  async updateQuoteWithBids(quoteId, payload) {
    return this.patch(API_ENDPOINTS.QUOTES.WITH_BIDS_BY_ID(quoteId), payload);
  }

  /**
   * Get all quotes
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (e.g., { status: 'sent' })
   * @returns {Promise<Array>} List of quotes
   */
  async getQuotes(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.QUOTES.BASE, {
      skip,
      limit,
      ...filters,
      // Merge so caller-supplied includes on `filters` are preserved AND
      // custom_profile is always requested (final key wins over the spread).
      include: mergeInclude(
        filters.include || 'bid_items,bid_items.bid_options,bid_items.bid_options.aircraft,bid_items.bid_options.operator,client,payments',
        'bid_items.bid_options.aircraft.custom_profile',
      ),
    });
  }

  /**
   * Get user's bids for track page (optimized endpoint)
   * @param {number} skip - Number of bids to skip
   * @param {number} limit - Maximum number of bids to return
   * @param {Object} filters - Optional filters (departure_date_from, departure_date_to, status, sort_by, sort_order)
   * @returns {Promise<Array>} List of bids with bid_options and aircraft only
   */
  async getMyTripsBids(skip = 0, limit = 10, filters = {}) {
    return this.get(API_ENDPOINTS.MY_TRIPS.BIDS, {
      skip,
      limit,
      ...filters,
      include: mergeInclude(filters.include, 'bid_options.aircraft.custom_profile'),
    });
  }

  /**
   * Get quote by ID
   * @param {string} quoteId - Quote ID
   * @returns {Promise<Object>} Quote object
   */
  async getQuoteById(quoteId) {
    return this.get(API_ENDPOINTS.QUOTES.BY_ID(quoteId), {
      include: mergeInclude(
        'bid_items,bid_items.bid_options,bid_items.bid_options.aircraft,bid_items.bid_options.operator,client,payments,creator,sender',
        'bid_items.bid_options.aircraft.custom_profile',
      ),
    });
  }

  /**
   * Get quote by ID using checkout token (for public access)
   * @param {string} quoteId - Quote ID
   * @param {string} token - Checkout token for authentication
   * @returns {Promise<Object>} Quote object
   */
  async getQuoteByIdWithToken(quoteId, token, extraInclude = '') {
    return this.get(
      API_ENDPOINTS.QUOTES.BY_ID(quoteId),
      {
        // extraInclude lets specific callers add relations without bloating
        // the default payload for every consumer (e.g. ClientTripSheet
        // requests 'passengers' so revisits show what the client already
        // submitted).
        include: mergeInclude(
          mergeInclude(
            'bid_items,bid_items.bid_options,bid_items.bid_options.aircraft,bid_items.bid_options.operator,client',
            'bid_items.bid_options.aircraft.custom_profile',
          ),
          extraInclude,
        ),
        ...(token && { token }),
      },
      token ? { skipAuth: true } : {}
    );
  }

  /**
   * Update a quote
   * @param {string} quoteId - Quote ID
   * @param {Object} updateData - Updated quote data
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Updated quote object
   */
  async updateQuote(quoteId, updateData, checkoutToken = null) {
    if (checkoutToken) {
      // Token-based public path (QuotePreviewPage's "mark VIEWED" PATCH,
      // signed-contract file save from the client side, etc.) — skipAuth
      // so we don't try to refresh a nonexistent auth session on public
      // visitors. Without this the PATCH hangs on the auth-refresh call,
      // and loadQuoteDetails' `await` blocks the "Loading quote..."
      // screen indefinitely.
      return this.patch(
        buildUrlWithParams(API_ENDPOINTS.QUOTES.BY_ID(quoteId), { token: checkoutToken }),
        updateData,
        { skipAuth: true },
      );
    }
    return this.patch(API_ENDPOINTS.QUOTES.BY_ID(quoteId), updateData);
  }

  async getQuotePassengers(quoteId) {
    return this.get(API_ENDPOINTS.QUOTES.PASSENGERS(quoteId));
  }

  async createQuotePassengersBatch(quoteId, passengers, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(buildUrlWithParams(API_ENDPOINTS.QUOTES.PASSENGERS_BATCH(quoteId), { token: checkoutToken }), passengers, {
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.QUOTES.PASSENGERS_BATCH(quoteId), passengers);
  }

  /**
   * Per-status + per-payment-status quote counts for the /quotes list
   * filter badges. Server owns role scoping (broker/junior_broker → own
   * rows; admin/finance → org; super_admin → all). Optional filters
   * (AND together): client_id, created_at_from/to, sent_at_from/to,
   * departure_date_from/to, as_of (+ as_of_field).
   *
   * Response shape:
   *   {
   *     total: number,
   *     status_summary: { SENT, DRAFT, ..., PASSENGERS_PENDING, ... },
   *     payment_status_summary: { NONE, CAPTURED, ... }
   *   }
   */
  async getQuotesSummary(filters = {}) {
    return this.get(API_ENDPOINTS.QUOTES.SUMMARY, filters);
  }

  // ==================== Clients ====================

  /**
   * Get all clients
   * @param {number} skip - Number of items to skip (pagination)
   * @param {number} limit - Maximum number of items to return
   * @param {Object} filters - Optional filters (name, email, etc.)
   * @returns {Promise<Array>} List of clients
   */
  async getClients(skip = 0, limit = 100, filters = {}) {
    return this.get(API_ENDPOINTS.CLIENTS.BASE, { skip, limit, ...filters });
  }

  /**
   * Header counts for the /clients page. Filter contract mirrors
   * getClients — same `search` OR-across-fields (first/last/full name,
   * company, email), same broker_id / broker_org_id / field filters.
   *
   * Response:
   *   { total, filtered_count }
   * `total` is the role-scoped grand total (search-independent);
   * `filtered_count` respects the search / field filters. When no
   * search is set, `filtered_count === total`.
   */
  async getClientsSummary(filters = {}) {
    return this.get(API_ENDPOINTS.CLIENTS.SUMMARY, filters);
  }

  /**
   * Create a new client
   * @param {Object} clientData - Client fields
   * @returns {Promise<Object>} Created client object
   */
  async createClient(clientData) {
    return this.post(API_ENDPOINTS.CLIENTS.BASE, clientData);
  }

  /**
   * Get client by ID
   * @param {string} clientId - Client ID
   * @returns {Promise<Object>} Client object
   */
  async getClientById(clientId, params = {}) {
    return this.get(API_ENDPOINTS.CLIENTS.BY_ID(clientId), params);
  }

  /**
   * Update a client
   * @param {string} clientId - Client ID
   * @param {Object} clientData - Updated client fields
   * @returns {Promise<Object>} Updated client object
   */
  async updateClient(clientId, clientData) {
    return this.patch(API_ENDPOINTS.CLIENTS.BY_ID(clientId), clientData);
  }

  /**
   * Update a client's Priority Commerce vault info (customer/card IDs returned
   * by the PAYMENT_METHOD_CAPTURE widget).
   * @param {string} clientId - Client ID
   * @param {Object} vaultData - Vault payload returned from the capture widget
   * @param {string|null} checkoutToken - public checkout token (token-based flow)
   * @returns {Promise<Object>} Updated client object
   */
  async updateClientPriorityVault(clientId, vaultData, checkoutToken = null) {
    if (checkoutToken) {
      return this.patch(buildUrlWithParams(API_ENDPOINTS.CLIENTS.PRIORITY_VAULT(clientId), { token: checkoutToken }), vaultData, {
        skipAuth: true
      });
    }
    return this.patch(API_ENDPOINTS.CLIENTS.PRIORITY_VAULT(clientId), vaultData);
  }

  async preAuthorizeMxMerchantPayment({ quoteId = null, invoiceId = null, cardId = null } = {}, checkoutToken = null) {
    const body = {};
    if (quoteId) body.quote_id = quoteId;
    if (invoiceId) body.invoice_id = invoiceId;
    if (cardId) body.card_id = cardId;

    if (checkoutToken) {
      return this.post(
        buildUrlWithParams(API_ENDPOINTS.MXMERCHANT.PRE_AUTHORIZE, { token: checkoutToken }),
        body,
        { skipAuth: true }
      );
    }
    return this.post(API_ENDPOINTS.MXMERCHANT.PRE_AUTHORIZE, body);
  }

  async getClientPassengers(clientId, checkoutToken = null) {
    if (checkoutToken) {
      return this.get(
        API_ENDPOINTS.CLIENTS.PASSENGERS(clientId),
        { token: checkoutToken },
        { skipAuth: true }
      );
    }
    return this.get(API_ENDPOINTS.CLIENTS.PASSENGERS(clientId));
  }

  async createClientPassengersBatch(clientId, passengers, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(buildUrlWithParams(API_ENDPOINTS.CLIENTS.PASSENGERS_BATCH(clientId), { token: checkoutToken }), passengers, {
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.CLIENTS.PASSENGERS_BATCH(clientId), passengers);
  }

  async updateClientPassenger(passengerId, data, checkoutToken = null) {
    if (checkoutToken) {
      return this.patch(buildUrlWithParams(API_ENDPOINTS.CLIENT_PASSENGERS.BY_ID(passengerId), { token: checkoutToken }), data, {
        skipAuth: true
      });
    }
    return this.patch(API_ENDPOINTS.CLIENT_PASSENGERS.BY_ID(passengerId), data);
  }

  async deleteClientPassenger(passengerId) {
    return this.delete(API_ENDPOINTS.CLIENT_PASSENGERS.BY_ID(passengerId));
  }

  async deleteQuotePassenger(passengerId, checkoutToken = null) {
    if (checkoutToken) {
      return this.delete(buildUrlWithParams(API_ENDPOINTS.QUOTE_PASSENGERS.BY_ID(passengerId), { token: checkoutToken }), {
        skipAuth: true
      });
    }
    return this.delete(API_ENDPOINTS.QUOTE_PASSENGERS.BY_ID(passengerId));
  }

  /**
   * Get all airports
   * @param {number} limit - Maximum number of airports to return
   * @returns {Promise<Array>} List of airports
   */
  async getAirportByIcao(icao) {
    const data = await this.get(`${API_ENDPOINTS.AIRPORTS.BASE}?icao=${icao}`);
    return Array.isArray(data) ? data[0] || null : data;
  }

  /**
   * Fuzzy typeahead search — BE `search=` param does ILIKE across code,
   * icao, name, city, state, city_code, and country (case-insensitive).
   * Returns an empty array when the trimmed query is shorter than 2 chars
   * (matches BE min-length) so callers don't need to guard themselves.
   *
   * Use this for typeahead inputs. For "I have a specific ICAO code and
   * want the airport details," use getAirportByIcao — cheaper exact match.
   */
  async searchAirports(query, { limit = 30 } = {}) {
    const q = String(query || '').trim();
    if (q.length < 2) return [];
    const data = await this.get(API_ENDPOINTS.AIRPORTS.BASE, { search: q, limit });
    return Array.isArray(data) ? data : (data?.data || []);
  }

  /**
   * Fetch the FBO list for an airport (by ICAO). Returns a flat array of FBOs.
   * @param {string} icao - Airport ICAO (e.g. "KSNA")
   * @returns {Promise<Array>}
   */
  async getFbos(icao) {
    if (!icao) return [];
    const data = await this.get(API_ENDPOINTS.AVIAPAGES.FBO, { airport_icao: icao });
    // Backend response has changed shape — accept both:
    //   new: { fbos: [...] }
    //   old: { fbos: { results: [...] } }
    const fbos = data?.fbos;
    if (Array.isArray(fbos)) return fbos;
    return fbos?.results || [];
  }

  /**
   * Send a message/email
   * @param {Object} messageData - Message data to send
   * @param {string} checkoutToken - Optional checkout token for public access
   * @returns {Promise<Object>} Sent message response
   */
  async sendMessage(messageData, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(buildUrlWithParams(API_ENDPOINTS.MESSAGES.SEND, { token: checkoutToken }), messageData, {
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.MESSAGES.SEND, messageData);
  }

  /**
   * Send an SMS message.
   * @param {{to_phone: string, message_body: string}} payload
   * @returns {Promise<Object>}
   */
  async sendSms(payload) {
    return this.post(API_ENDPOINTS.MESSAGES.SMS_SEND, payload);
  }

  // ==================== Checkout ====================

  /**
   * Create a checkout token
   * @param {Object} tokenData - Token data for checkout
   * @param {boolean} [useImageBaseUrl=false] - When true, sends the request to REACT_APP_IMAGE_BASE_URL instead of the default API base
   * @returns {Promise<Object>} Checkout token response
   */
  async createCheckoutToken(tokenData, useImageBaseUrl = false) {
    if (useImageBaseUrl) {
      return this.post(API_ENDPOINTS.CHECKOUT.TOKEN, tokenData, {
        baseUrl: process.env.REACT_APP_IMAGE_BASE_URL,
      });
    }
    return this.post(API_ENDPOINTS.CHECKOUT.TOKEN, tokenData);
  }

  /**
   * Create a tracking token for public flight tracking access
   * @param {string} quoteId - Quote ID
   * @param {number} legNumber - Leg number
   * @returns {Promise<Object>} Tracking token response
   */
  async createTrackingToken(quoteId, legNumber) {
    return this.post(`${API_ENDPOINTS.FLIGHTRADAR24.TOKEN}?quote_id=${encodeURIComponent(quoteId)}&leg_number=${encodeURIComponent(legNumber)}`);
  }

  /**
   * Create a payment intent for Stripe checkout
   * @param {Object} paymentData - Payment intent data
   * @param {string} paymentData.amount - Amount to charge
   * @param {string} paymentData.currency - Currency code (e.g., 'USD')
   * @param {string} paymentData.quote_id - Quote ID
   * @param {string} paymentData.description - Payment description
   * @param {string} [checkoutToken] - Optional checkout token for public access
   * @returns {Promise<Object>} Payment intent response with client_secret
   */
  /**
   * Send an invoice via Mercury
   * @param {Object} invoiceData - Invoice data
   * @returns {Promise<Object>} Invoice response
   */
  async sendInvoice(invoiceData) {
    return this.post(API_ENDPOINTS.MERCURY.INVOICE, invoiceData);
  }

  // ========================================
  // Invoices API Methods
  // ========================================

  /**
   * Create a new invoice
   * @param {Object} invoiceData - Invoice payload
   * @param {string} invoiceData.broker_org_id
   * @param {string} invoiceData.created_by
   * @param {string} invoiceData.client_id
   * @param {string} invoiceData.quote_id
   * @param {string} invoiceData.flight_id
   * @param {string} invoiceData.invoice_type
   * @param {number} invoiceData.platform_fee_percent
   * @param {string} invoiceData.invoice_number
   * @param {Array}  invoiceData.items
   * @param {string} invoiceData.status
   * @returns {Promise<Object>} Created invoice
   */
  async createInvoice(invoiceData) {
    return this.post(API_ENDPOINTS.INVOICES.BASE, invoiceData);
  }

  async getInvoices(filters = {}) {
    const data = await this.get(API_ENDPOINTS.INVOICES.BASE, filters);
    return Array.isArray(data) ? data : (data?.items || data?.data || data?.invoices || []);
  }

  async getInvoiceById(invoiceId, token = null, params = {}) {
    return this.get(
      API_ENDPOINTS.INVOICES.BY_ID(invoiceId),
      { ...params, ...(token ? { token } : {}) },
      token ? { skipAuth: true } : {}
    );
  }

  async updateInvoice(invoiceId, updates, token = null) {
    if (token) {
      return this.patch(
        buildUrlWithParams(API_ENDPOINTS.INVOICES.BY_ID(invoiceId), { token }),
        updates,
        { skipAuth: true }
      );
    }
    return this.patch(API_ENDPOINTS.INVOICES.BY_ID(invoiceId), updates);
  }

  // ========================================
  // Financial Reports
  // ========================================

  /**
   * Server-driven Financials list. Returns a unified page of quote + invoice
   * rows (each carries `row_type: 'quote' | 'invoice'`). Filter contract is
   * shared with getFinancialReportsSummary — anything that shapes the KPI
   * cards should shape this list identically.
   *
   * Filters (all optional):
   *   status_search    — comma-separated (OR across quote + invoice statuses)
   *   client_id        — uuid
   *   quote_id         — uuid
   *   invoice_id       — uuid (setting this excludes quote rows)
   *   reference_id     — uuid (matches quote_id OR invoice's linked quote)
   *   reference_no     — exact quote_number OR invoice_number
   *   created_by       — uuid (admin/finance/super_admin only; ignored for
   *                     broker/junior_broker who are always scoped to self)
   *   search           — min 2 chars, case-insensitive substring; OR across
   *                     quote_number, invoice_number, client name, creator
   *                     name, airports, operator name, tail number
   *   created_at_from  — ISO date or datetime (inclusive)
   *   created_at_to    — ISO date or datetime (exclusive; YYYY-MM-DD is
   *                     treated as inclusive-that-UTC-day server-side)
   *   sort_by          — created_at | departure_date | total_taxes |
   *                     total_amount | client_name  (default created_at)
   *   sort_order       — asc | desc  (default desc)
   *   skip             — pagination offset (default 0)
   *   limit            — page size (default 100, max 200). OMIT BOTH `skip`
   *                     and `limit` to fetch the full filtered set (used by
   *                     CSV export). Server enforces a soft cap.
   *   include          — comma-separated related data; see BE docs
   */
  async getFinancialReports(filters = {}) {
    const data = await this.get(API_ENDPOINTS.FINANCIAL_REPORTS.BASE, filters);
    // BE returns a bare array; accept the object-wrapped variants too so a
    // future change to `{ items, total, has_more }` doesn't break callers.
    if (Array.isArray(data)) return { items: data, total: null, has_more: null };
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: typeof data?.total === 'number' ? data.total : null,
      has_more: typeof data?.has_more === 'boolean' ? data.has_more : null,
    };
  }

  /**
   * KPI totals for the Financials summary cards. Accepts the same filter
   * contract as getFinancialReports (minus skip/limit/sort_by/sort_order/
   * include). Returns totals over the full matching set — total_revenue and
   * total_commissions are paid-only aggregations on the server side.
   */
  async getFinancialReportsSummary(filters = {}) {
    return this.get(API_ENDPOINTS.FINANCIAL_REPORTS.SUMMARY, filters);
  }

  // ========================================
  // Payments
  // ========================================

  async getPayments(filters = {}) {
    const data = await this.get(API_ENDPOINTS.PAYMENTS.BASE, filters);
    return Array.isArray(data) ? data : (data?.items || data?.data || data?.payments || []);
  }

  // ========================================
  // FlightRadar24 API Methods
  // ========================================

  // Flights
  async getFlights(filters = {}) {
    return this.get(API_ENDPOINTS.FLIGHTS.BASE, {
      ...filters,
      // Note: /flights uses the plural join alias `aircrafts` (not
      // `aircraft`) because that's how the BE relates the flight row
      // to its aircraft record. Every other endpoint keeps the
      // singular `aircraft.custom_profile`.
      include: mergeInclude(filters.include, 'aircrafts.custom_profile'),
    });
  }

  async createFlight(flightData) {
    return this.post(API_ENDPOINTS.FLIGHTS.BASE, flightData);
  }

  async getFlightById(flightId) {
    return this.get(API_ENDPOINTS.FLIGHTS.BY_ID(flightId), {
      include: 'aircrafts.custom_profile',
    });
  }

  async updateFlight(flightId, flightData) {
    return this.patch(API_ENDPOINTS.FLIGHTS.BY_ID(flightId), flightData);
  }

  async patchFlight(flightId, flightData) {
    return this.patch(API_ENDPOINTS.FLIGHTS.BY_ID(flightId), flightData);
  }

  async createFlightsBatch(flightsData) {
    return this.post(API_ENDPOINTS.FLIGHTS.BATCH, flightsData);
  }

  async updateFlightStatus(quoteId, flightStatus) {
    return this.patch(API_ENDPOINTS.FLIGHTS.BASE, { quote_id: quoteId, flight_status: flightStatus });
  }

  async getLiveFlightPositions(registrations, params = {}) {
    return this.get(API_ENDPOINTS.FLIGHTRADAR24.LIVE_POSITIONS, { registrations, ...params });
  }

  /**
   * FR24 flight-summary endpoint. Two call shapes:
   *   1. Legacy positional  — getFlightSummary(registrations, from, to)
   *   2. Options bag        — getFlightSummary({ registrations?, flightIds?, from?, to? })
   *
   * FR24 requires EITHER flight_ids OR (registrations + from + to). Windows
   * must not exceed 14 days. Both rules are enforced client-side here so a
   * bad shape fails fast without a wasted round-trip. ISO strings get the
   * milliseconds stripped — the BE quietly returns empty otherwise.
   */
  async getFlightSummary(...args) {
    const stripMs = (iso) =>
      typeof iso === 'string' ? iso.replace(/\.\d{3}Z$/, 'Z') : iso;

    const opts = (args.length === 1 && args[0] && typeof args[0] === 'object')
      ? args[0]
      : { registrations: args[0], from: args[1], to: args[2] };

    const { flightIds, registrations, from, to } = opts;

    // Guardrail 01 — reject empty filter combos.
    const hasIds = flightIds != null && String(flightIds).length > 0;
    const hasWindow = registrations && from && to;
    if (!hasIds && !hasWindow) {
      throw new Error(
        'getFlightSummary: pass flight_ids OR (registrations + from + to). See Flight Summary Patterns.'
      );
    }

    // Guardrail 02 — reject windows over 14 days. Use Pattern 04
    // (getTailHistory) for longer spans.
    if (!hasIds && hasWindow) {
      const spanMs = new Date(to).getTime() - new Date(from).getTime();
      const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
      if (!Number.isFinite(spanMs) || spanMs < 0) {
        throw new Error('getFlightSummary: invalid date range (from/to must be parseable ISO strings, to > from).');
      }
      if (spanMs > FOURTEEN_DAYS_MS) {
        throw new Error(
          `getFlightSummary: window ${(spanMs / 86_400_000).toFixed(1)}d exceeds the 14d cap. Use getTailHistory for longer traces.`
        );
      }
    }

    const params = hasIds
      ? { flight_ids: Array.isArray(flightIds) ? flightIds.join(',') : String(flightIds) }
      : {
          registrations,
          flight_datetime_from: stripMs(from),
          flight_datetime_to: stripMs(to),
        };
    return this.get(API_ENDPOINTS.FLIGHTRADAR24.FLIGHT_SUMMARY, params);
  }

  /**
   * Historical trace for a tail across an arbitrary day span (Pattern 04).
   * Splits the range into ≤13-day chunks (one under the 14d cap), fans out
   * in parallel, dedupes by fr24_id, sorts by datetime_takeoff desc.
   *
   * @param {string} registrations - Tail number (single or comma-separated).
   * @param {number} days - How many days back from now to trace. Default 30.
   */
  async getTailHistory(registrations, days = 30) {
    if (!registrations) throw new Error('getTailHistory: registrations is required.');
    if (!(days > 0)) throw new Error('getTailHistory: days must be positive.');

    const CHUNK_DAYS = 13;
    const DAY_MS = 86_400_000;
    const now = Date.now();
    const rangeStart = now - days * DAY_MS;

    const windows = [];
    for (let end = now; end > rangeStart; ) {
      const start = Math.max(end - CHUNK_DAYS * DAY_MS, rangeStart);
      windows.push([new Date(start).toISOString(), new Date(end).toISOString()]);
      end = start;
    }

    const chunks = await Promise.all(
      windows.map(([from, to]) => this.getFlightSummary({ registrations, from, to }))
    );

    const seen = new Set();
    const legs = chunks
      .flatMap((r) => (Array.isArray(r) ? r : r?.data || []))
      .filter((leg) => {
        const id = leg?.fr24_id ?? leg?.flight_id;
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .sort(
        (a, b) => new Date(b.datetime_takeoff).getTime() - new Date(a.datetime_takeoff).getTime()
      );
    return legs;
  }

  async getAirportFull(code, params = {}) {
    return this.get(API_ENDPOINTS.FLIGHTRADAR24.AIRPORT(code), params);
  }

  async getFlightTracks(flightId, params = {}) {
    return this.get(API_ENDPOINTS.FLIGHTRADAR24.FLIGHT_TRACKS, { flight_id: flightId, ...params });
  }

  async createCheckoutSession(sessionData, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(buildUrlWithParams(API_ENDPOINTS.CHECKOUT.SESSION, { token: checkoutToken }), sessionData, {
        skipAuth: true
      });
    }
    return this.post(API_ENDPOINTS.CHECKOUT.SESSION, sessionData);
  }

  async createPaymentIntent(paymentData, checkoutToken = null) {
    if (checkoutToken) {
      return this.post(buildUrlWithParams(API_ENDPOINTS.CHECKOUT.PAYMENTINTENTS, { token: checkoutToken }), paymentData);
    }
    return this.post(API_ENDPOINTS.CHECKOUT.PAYMENTINTENTS, paymentData);
  }

  // ========================================
  // AWS S3 API Methods
  // ========================================

  /**
   * Get a signed URL for an S3 object
   * @param {string} key - S3 object key (e.g., "aircraft/images/N12345/photo.jpg")
   * @param {string} fileType - MIME type of the file (e.g., "image/jpeg")
   * @returns {Promise<Object>} Signed URL response
   */
  async getS3SignedUrl(key, fileType) {
    return this.get(API_ENDPOINTS.AWS.S3_SIGNED_URL, { key, fileType });
  }

  /**
   * Get a pre-signed S3 URL using a public checkout token (no auth).
   * Used from the public /quotepreview page when the client signs the
   * contract — the browser has the checkout token but no broker session.
   * Backend verifies the token maps to the quote before issuing the URL.
   * @param {string} key - S3 key (e.g. contracts/{quoteId}/signed_{ts}.pdf)
   * @param {string} fileType - MIME type
   * @param {string} token - Checkout token
   * @returns {Promise<Object>} { uploadUrl, publicUrl }
   */
  async getS3SignedUrlWithToken(key, fileType, token) {
    return this.get(
      API_ENDPOINTS.AWS.S3_SIGNED_URL,
      { key, fileType, ...(token && { token }) },
      token ? { skipAuth: true } : {}
    );
  }

  async uploadFileToS3(file, s3Urls){
    const uploadRes = await fetch(s3Urls.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file,
    });

    if (!uploadRes.ok) throw new Error(`S3 upload failed: ${uploadRes.status}`);

    return s3Urls.publicUrl;
  }

  /**
   * Delete a single S3 object by key.
   *
   * TODO(BE): This endpoint isn't live yet. Every callsite that would
   * fire this method is kept in source-controlled files but commented
   * out with a matching TODO(BE) marker — search the repo for that
   * string to find them, then uncomment when the BE delete endpoint
   * ships. Photos removed while this stays disabled stay parked in
   * the bucket (data-safe — no other org's photo can be touched — but
   * accumulates dead objects over time).
   *
   * When the BE endpoint arrives:
   *  1. Fix S3_DELETE_OBJECT in apiEndpoints.js to the real path.
   *  2. Adjust the DELETE body shape below if BE expects params.
   *  3. Uncomment the callsites (grep TODO(BE)-S3-DELETE).
   *
   * @param {string} key - S3 object key (e.g. photos/{org_id}/{TAIL}/…)
   */
  async deleteS3Object(key) {
    // eslint-disable-next-line no-console
    console.warn('[deleteS3Object] BE endpoint not implemented yet — no-op.', { key });
    return { skipped: true, key };
    // Uncomment once the endpoint is live:
    // return this.delete(
    //   buildUrlWithParams(API_ENDPOINTS.AWS.S3_DELETE_OBJECT, { key }),
    // );
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();

// Export class for testing purposes
export { ApiClient };

// ============================================================================
// quote_number helpers — fresh number generation + retry-on-collision wrapper
// ============================================================================

const QUOTE_NUMBER_MAX_RETRIES = 5;

/**
 * Generate a fresh `quote_number`. Includes a 3-digit random suffix so two
 * brokers tapping Generate/Duplicate within the same millisecond do not
 * collide on the Postgres UniqueViolation.
 */
export function freshQuoteNumber() {
  const suffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `TR-${Date.now()}-${suffix}`;
}

/**
 * Detect a UniqueViolation on the `quote_number` column. The backend surfaces
 * Postgres errors in `error.message` / `error.detail`; we match conservatively
 * on the column name plus a unique/duplicate keyword.
 */
export function isQuoteNumberConflict(err) {
  const haystack = `${err?.message || ''} ${err?.detail || ''} ${JSON.stringify(err?.details || '')}`.toLowerCase();
  return haystack.includes('quote_number') && (
    haystack.includes('unique') || haystack.includes('duplicate') || haystack.includes('already exists')
  );
}

/**
 * Wrap `apiClient.createQuote` with random-suffix retry on quote_number
 * UniqueViolation. On conflict, regenerate `body.quote_number` and try again
 * (up to QUOTE_NUMBER_MAX_RETRIES times). Any non-conflict error is rethrown
 * immediately so callers still see real failures.
 */
export async function createQuoteWithRetry(body) {
  let lastError = null;
  for (let attempt = 1; attempt <= QUOTE_NUMBER_MAX_RETRIES; attempt++) {
    try {
      return await apiClient.createQuote(body);
    } catch (err) {
      lastError = err;
      if (!isQuoteNumberConflict(err)) throw err;
      body.quote_number = freshQuoteNumber();
    }
  }
  throw lastError;
}
